import React, { useEffect, useState } from 'react'
import { MeterChart, TableBox } from './component/Charts'
import { useLocation, useNavigate } from 'react-router-dom'
import useAxiosPrivate from '../../hooks/useAxiosPrivate'
import axios from 'axios'

import { FaRegLightbulb } from 'react-icons/fa'
import { IoCartOutline, IoCarSportOutline, IoTriangle } from 'react-icons/io5'
import AltColChart from './component/Charts/AltColChart'
import ApexGaugeChart from './component/Charts/ApexGaugeChart'
import SiteEmission from './SiteEmission'

const Statistics = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const axiosPrivate = useAxiosPrivate()

  const [data, setData] = useState({})
  const [year, setYear] = useState(new Date().getFullYear());
  const [site, setSite] = useState('')
  const [sitesList, setSitesList] = useState([])
  const [isLoading, setIsLoading] = useState(false)
  const [baseYearTotal, setBaseYearTotal] = useState(0)
  const [recordedYears, setRecordedYears] = useState([new Date().getFullYear()])

  const getOrderedSitesWithDepth = (sites) => {
    if (!sites || sites.length === 0) return [];
    const map = new Map();
    sites.forEach((s) => map.set(s.id, { ...s, children: [] }));

    const roots = [];
    sites.forEach((s) => {
      if (s.parentSiteId && map.has(s.parentSiteId)) {
        map.get(s.parentSiteId).children.push(map.get(s.id));
      } else {
        roots.push(map.get(s.id));
      }
    });

    const ordered = [];
    const traverse = (node, depth = 0) => {
      ordered.push({ ...node, depth });
      if (node.children) {
        node.children.forEach((child) => traverse(child, depth + 1));
      }
    };

    roots.forEach((root) => traverse(root, 0));
    return ordered;
  };

  useEffect(() => {
    let isMounted = true;
    const fetchSites = async () => {
      try {
        const response = await axiosPrivate.get('/sites');
        if (isMounted) setSitesList(response.data);
      } catch (error) {
        console.error("Error fetching sites:", error);
      }
    };
    fetchSites();
    return () => { isMounted = false; };
  }, [axiosPrivate]);

  useEffect(() => {
    let isMounted = true;
    const fetchRecordedYears = async () => {
      try {
        const endpoint = site ? `/dashboards/recorded-years?siteId=${site}` : '/dashboards/recorded-years';
        const response = await axiosPrivate.get(endpoint);
        if (isMounted && response?.data?.years?.length > 0) {
          setRecordedYears(response.data.years);
          if (!response.data.years.includes(year)) {
            setYear(response.data.years[0]);
          }
        }
      } catch (error) {
        console.error("Error fetching recorded years:", error);
      }
    };
    fetchRecordedYears();
    return () => { isMounted = false; };
  }, [axiosPrivate, site]);

  useEffect(() => {
    if (!year) return;

    let isMounted = true;
    const controller = new AbortController();

    const fetchData = async () => {
      setIsLoading(true);
      try {
        const urlToFetch = site
          ? `/dashboards/total-emissions/${year}?siteId=${site}`
          : `/dashboards/total-emissions/${year}`;

        const response = await axiosPrivate.get(urlToFetch, {
          signal: controller.signal,
        });

        if (isMounted && response?.data) {
          const currentData = response.data;
          setData(currentData);

          const baseYearType = localStorage.getItem('baseYearType') || 'rolling';
          const fixedBaseYear = Number(localStorage.getItem('fixedBaseYear')) || (new Date().getFullYear() - 1);
          const baseYear = baseYearType === 'rolling' ? (year - 1) : fixedBaseYear;

          let baseTotal = 0;
          if (baseYear === year) {
            baseTotal = currentData.grandTotal || 0;
          } else {
            const baseUrlToFetch = site
              ? `/dashboards/total-emissions/${baseYear}?siteId=${site}`
              : `/dashboards/total-emissions/${baseYear}`;
            try {
              const baseResponse = await axiosPrivate.get(baseUrlToFetch, {
                signal: controller.signal,
              });
              baseTotal = baseResponse?.data?.grandTotal || 0;
            } catch (err) {
              console.error('Failed to fetch base year data:', err);
            }
          }
          if (isMounted) {
            setBaseYearTotal(baseTotal);
          }
        }
      } catch (err) {
        if (!axios.isCancel(err)) {
          console.error('Failed to fetch dashboard data:', err);
          if (err.response?.status === 403) {
            navigate('/sign-in', { state: { from: location }, replace: true });
          }
        }
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    fetchData();

    return () => {
      isMounted = false;
      controller.abort();
    };
  }, [year, site, axiosPrivate, navigate, location]);


  const handleAddNewSubmit = (e) => e.preventDefault()

  const baseYearType = localStorage.getItem('baseYearType') || 'rolling';
  const label = baseYearType === 'fixed' ? 'Base Year Chg' : 'YoY';
  const currentTotal = data?.grandTotal || 0;
  let changePercentage = 0;
  if (baseYearTotal > 0) {
    changePercentage = ((currentTotal - baseYearTotal) / baseYearTotal) * 100;
  } else if (currentTotal > 0) {
    changePercentage = 100;
  }
  const isIncrease = changePercentage >= 0;

  return (
    <div className="w-full h-full overflow-y-auto max-h-full max-w-full">
      {/* Site & Year Selection */}
      <div className="justify-start ml-4 mt-4">
        <form onSubmit={handleAddNewSubmit} className="flex gap-x-4">
          <div>
            <select
              className="bg-gray-50 border text-gray-900 text-sm rounded-lg block w-full p-2.5 dark:bg-gray-700 dark:text-white"
              value={site}
              onChange={(e) => setSite(e.target.value)}
            >
              <option value="">Company/Site (All)</option>
              {getOrderedSitesWithDepth(sitesList).map((s) => (
                <option key={s.id} value={s.id}>
                  {'\u00A0\u00A0\u00A0\u00A0'.repeat(s.depth)}{s.depth > 0 ? '└─ ' : ''}{s.name}
                </option>
              ))}
            </select>
          </div>

          <div>
            <select
              className="bg-gray-50 border text-gray-900 text-sm rounded-lg block w-full p-2.5 dark:bg-gray-700 dark:text-white"
              value={year}
              onChange={(e) => setYear(Number(e.target.value))}
            >
              <option value="">Year</option>
              {recordedYears.map((y) => (
                <option key={y} value={y}>
                  {y}
                </option>
              ))}
            </select>
          </div>
        </form>
      </div>

      {/* Conditional Rendering */}
      {site !== '' ? (
        <SiteEmission siteName={sitesList.find(s => s.id === site)?.name || site} siteData={data} year={year} />
      ) : (
        // Default Company Dashboard
        <div className="grid pl-4 pr-4 rounded-3xl pt-4 text-maindarkbg dark:text-mainbg">
          <div className="grid grid-cols-12 gap-6">
            <div className="grid grid-cols-12 col-span-12 gap-6 xxl:col-span-9">
              {/* Total Emission Gauge */}
              <div className="col-span-12 md:col-span-6">
                <div className="bg-white shadow-lg flex flex-col lg:flex-row items-center border dark:bg-darkbg4 rounded-md h-full py-4 pr-0 lg:pr-8">
                  <ApexGaugeChart
                    amount={currentTotal}
                    maxAmount={baseYearTotal}
                    title="Total Emission"
                  />
                  <div className="flex flex-col gap-y-5">
                    <div className="text-lg">Total Emissions</div>
                    <div className="font-black text-4xl">
                      {data?.grandTotal?.toFixed(2)}
                      <span className="font-semibold text-lg ml-3"> tCO2e </span>
                    </div>
                     <div className="font-semibold flex items-center gap-3 text-lg">
                      {label}{' '}
                      <IoTriangle
                        className={`${
                          isIncrease ? 'text-red-500' : 'text-green-500 rotate-180 transform'
                        } transition-transform duration-200`}
                      />{' '}
                      {Math.abs(changePercentage).toFixed(2)}%
                    </div>
                  </div>
                </div>
              </div>

              {/* Carbon Footprint & Table */}
                <div className="grid grid-rows-5 md:grid-rows-none gap-6 col-span-12 md:col-span-6 h-auto md:h-full max-h-96 mt-6 md:mt-0">
                  <div className="row-span-1 md:row-span-2 grid grid-cols-2 bg-white shadow-lg items-center justify-center flex border dark:bg-darkbg4 rounded-md h-auto md:max-h-none md:min-h-36">
                    <span className="font-semibold text-base md:text-lg flex items-center justify-center">
                      Carbon Footprint <br /> per Employee
                    </span>
                    <span className="font-semibold text-base md:text-lg flex items-center justify-center">
                      <span className="font-black text-2xl md:text-4xl mr-6"> {data?.grandTotal?.toFixed(2)} </span>
                      tCO2e
                    </span>
                  </div>
                </div>
              </div>

              {/* Carbon Offset Gauge */}
              {/* <div className="col-span-12 md:col-span-6">
                <div className="bg-white shadow-lg flex flex-col lg:flex-row items-center border dark:bg-darkbg4 rounded-md h-full py-4 pr-0 lg:pr-8">
                  <ApexGaugeChart amount={75} maxAmount={100} title="Carbon Offset (tCO2e)" />
                  <div className="flex flex-col items-center lg:items-start gap-y-5">
                    <div className="text-lg">Carbon Offset (tCO2e)</div>
                    <div className="font-black text-4xl mr-6">
                      4.51
                      <span className="font-semibold text-lg ml-3"> tCO2e </span>
                    </div>
                    <div className="font-semibold flex items-center gap-3 text-lg">
                      YoY <IoTriangle className="text-green-500" /> 49.4%
                    </div>
                  </div>
                </div>
              </div> */}

              {/* Scope Breakdown */}
              <div className="col-span-12 grid-none grid-cols-none md:grid md:grid-cols-12 gap-6 h-auto">
                <div className="col-span-12 md:col-span-6 bg-white shadow-lg flex flex-col items-center border dark:bg-darkbg4 rounded-md max-h-96 py-4 gap-6">
                  <span className="font-semibold text-lg flex justify-start w-full px-4">
                    Scope Breakdown % of Total Emission
                  </span>
                  <MeterChart Icon={IoCarSportOutline} scope={1} amount={data?.totalScope1} maxAmount={data?.grandTotal} barColor="bg-scope1" />
                  <MeterChart Icon={FaRegLightbulb} scope={2} amount={data?.totalScope2} maxAmount={data?.grandTotal} barColor="bg-scope2" />
                  <MeterChart Icon={IoCartOutline} scope={3} amount={data?.totalScope3} maxAmount={data?.grandTotal} barColor="bg-scope3" />
                </div>

                {/* Category Table */}
                <div className="grid grid-rows-5 md:grid-rows-none gap-6 col-span-12 md:col-span-6 h-auto md:h-full max-h-96 mt-6 md:mt-0">
                  <TableBox
                    rowSpan="row-span-4 md:row-span-3"
                    topCategories={data?.categoryBreakdown || []}
                  />
              </div>

              {/* Column Chart */}
              <div className="col-span-12 rounded-md shadow-lg dark:bg-darkbg4 mb-10">
                <AltColChart
                  data={data.monthlyByScope}
                  title="Monthly Emission by Scopes"
                  height={350}
                />
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default Statistics
